import React, { useMemo, useState, useEffect } from 'react';
import Lightbox from '../Lightbox/Lightbox';
import './ImageCarousel.css';

const buildImageFallbackSources = (url = '') => {
  const primary = String(url || '').trim();
  if (!primary) return [];
  const fallback = /^https?:\/\//i.test(primary)
    ? `https://images.hive.blog/0x0/${primary}`
    : '';
  return Array.from(new Set([primary, fallback].filter(Boolean)));
};

const ImageCarousel = ({ images = [] }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const normalizedImages = useMemo(() => (
    Array.isArray(images)
      ? images
        .map((entry, index) => {
          if (typeof entry === 'string') {
            const url = String(entry || '').trim();
            if (!url) return null;
            return { url, label: `[Image ${index + 1}]` };
          }
          if (entry && typeof entry === 'object') {
            const url = String(entry.url || entry.src || '').trim();
            if (!url) return null;
            return { url, label: String(entry.label || `[Image ${index + 1}]`) };
          }
          return null;
        })
        .filter(Boolean)
      : []
  ), [images]);

  const imageCandidates = useMemo(
    () => normalizedImages.map((item) => buildImageFallbackSources(item.url)),
    [normalizedImages]
  );
  const [sourceIndexes, setSourceIndexes] = useState([]);

  useEffect(() => {
    setSourceIndexes(imageCandidates.map(() => 0));
  }, [imageCandidates]);

  const getActiveImageUrl = (index) => {
    const candidates = imageCandidates[index] || [];
    const sourceIndex = sourceIndexes[index] || 0;
    return candidates[sourceIndex] || normalizedImages[index]?.url || '';
  };

  const handleImageError = (index) => {
    setSourceIndexes((prev) => {
      const next = [...prev];
      const candidates = imageCandidates[index] || [];
      const current = next[index] || 0;
      if (current + 1 < candidates.length) {
        next[index] = current + 1;
      }
      return next;
    });
  };

  const lightboxImages = normalizedImages.map((_item, index) => getActiveImageUrl(index)).filter(Boolean);

  if (!normalizedImages || normalizedImages.length === 0) return null;

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const lightboxPrevious = () => {
    setLightboxIndex((prevIndex) => 
      prevIndex === 0 ? normalizedImages.length - 1 : prevIndex - 1
    );
  };

  const lightboxNext = () => {
    setLightboxIndex((prevIndex) => 
      prevIndex === normalizedImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  if (normalizedImages.length === 1) {
    return (
      <>
        <div className="single-image-container" onClick={() => openLightbox(0)}>
          <img 
            src={getActiveImageUrl(0)}
            alt={normalizedImages[0].label}
            title={normalizedImages[0].label}
            className="carousel-image single-image"
            style={{ cursor: 'pointer' }}
            onError={() => handleImageError(0)}
          />
        </div>
        <div className="carousel-image-title">{normalizedImages[0].label}</div>
        {lightboxOpen && (
          <Lightbox 
            images={lightboxImages}
            currentIndex={lightboxIndex}
            onClose={closeLightbox}
            onPrevious={lightboxPrevious}
            onNext={lightboxNext}
          />
        )}
      </>
    );
  }

  const goToPrevious = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? normalizedImages.length - 1 : prevIndex - 1
    );
  };

  const goToNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex((prevIndex) => 
      prevIndex === normalizedImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const goToSlide = (index, e) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentIndex(index);
  };

  return (
    <>
      <div className="image-carousel">
        {/* Main Image Display */}
        <div className="carousel-main" onClick={() => openLightbox(currentIndex)}>
          <img 
            src={getActiveImageUrl(currentIndex)}
            alt={normalizedImages[currentIndex].label}
            title={normalizedImages[currentIndex].label}
            className="carousel-image"
            style={{ cursor: 'pointer' }}
            onError={() => handleImageError(currentIndex)}
          />

          {/* Navigation Arrows */}
          <button 
            className="carousel-nav carousel-prev"
            onClick={goToPrevious}
            title="Previous image"
            aria-label="Previous image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
          </button>

          <button 
            className="carousel-nav carousel-next"
            onClick={goToNext}
            title="Next image"
            aria-label="Next image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </button>

          {/* Image Counter */}
          <div className="carousel-counter">
            {currentIndex + 1} / {normalizedImages.length}
          </div>
        </div>
        <div className="carousel-image-title">{normalizedImages[currentIndex].label}</div>
        {/* Dot Navigation */}
        <div className="carousel-dots">
          {normalizedImages.map((entry, index) => (
            <button
              key={index}
              className={`carousel-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={(e) => goToSlide(index, e)}
              title={entry.label}
              aria-label={entry.label}
            />
          ))}
        </div>
      </div>

      {lightboxOpen && (
        <Lightbox 
          images={lightboxImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrevious={lightboxPrevious}
          onNext={lightboxNext}
        />
      )}
    </>
  );
};

export default ImageCarousel;